import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Form, Input, DatePicker, Typography, Space, Card, Row, Col, message } from 'antd';
import { SearchOutlined, LineChartOutlined, ApartmentOutlined } from '@ant-design/icons';
import API from '../services/api'; // 导入统一的API实例
import * as echarts from 'echarts'; // 导入ECharts
import dayjs from 'dayjs';

const { Title } = Typography;

const HuBeiXiaoQiangTrafficQuery = () => {
  const [ip, setIp] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [trafficData, setTrafficData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 初始化默认时间范围（当天）
  useEffect(() => {
    const today = dayjs();
    setStartTime(today);
    setEndTime(today);
  }, []);

  // 验证日期范围
  const validateDateRange = (start, end) => {
    if (!start || !end) {
      return { isValid: false, message: '请选择开始时间和结束时间' };
    }

    if (start.isAfter(end)) {
      return { isValid: false, message: '开始时间不能晚于结束时间' };
    }

    const daysDiff = end.diff(start, 'day') + 1;
    if (daysDiff > 3) {
      return { isValid: false, message: '查询时间跨度不能超过3天' };
    }

    return { isValid: true, message: '' };
  };

  // 查询单个IP流量
  const handleIpTrafficQuery = async () => {
    // 验证IP地址
    if (!ip.trim()) {
      message.warning('请输入IP地址');
      return;
    }

    // 验证日期范围
    const validation = validateDateRange(startTime, endTime);
    if (!validation.isValid) {
      message.warning(validation.message);
      return;
    }

    setLoading(true);
    try {
      const response = await API.get('/api/admin/get-flow-log-data', {
        params: {
          ip: ip.trim(),
          start_time: startTime ? startTime.format('YYYY-MM-DD') : '',
          end_time: endTime ? endTime.format('YYYY-MM-DD') : ''
        }
      });
      
      if (response.data.length === 0) {
        message.info('暂无数据');
        setTrafficData([]);
      } else {
        setTrafficData(response.data);
        message.success('查询成功');
      }
    } catch (error) {
      console.error('获取单个IP流量失败:', error);
      message.error(error.response?.data?.message || '获取流量数据失败');
      setTrafficData([]);
    } finally {
      setLoading(false);
    }
  };

  const getUnit = (maxValue) => {
    if (maxValue >= 1e9) return { divisor: 1e9, unit: 'Gbps' };
    if (maxValue >= 1e6) return { divisor: 1e6, unit: 'Mbps' };
    if (maxValue >= 1e3) return { divisor: 1e3, unit: 'Kbps' };
    return { divisor: 1, unit: 'bps' };
  };

  const drawTrafficChart = useCallback((chart, dataArray) => {
    const labels = dataArray.map(data => {
      const formattedHour = data.hour < 10 ? `0${data.hour}` : data.hour;
      const formattedMinute = data.minute === 0 ? '00' : data.minute;
      return `${formattedHour}:${formattedMinute}`; // 显示完整的时间
    });

    const inSizeRaw = dataArray.map(data => (data.in_size * 8));  
    const outSizeRaw = dataArray.map(data => (data.out_size * 8));
    const inDropSizeRaw = dataArray.map(data => (data.in_drop_size * 8));  // 增加拦截流量

    const maxValue = Math.max(...inSizeRaw, ...outSizeRaw, ...inDropSizeRaw);
    const { divisor, unit } = getUnit(maxValue);

    const inSize = inSizeRaw.map(size => (size / divisor).toFixed(2));
    const outSize = outSizeRaw.map(size => (size / divisor).toFixed(2));
    const inDropSize = inDropSizeRaw.map(size => (size / divisor).toFixed(2));  // 处理拦截流量

    const trafficOption = {
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#d9d9d9',
        textStyle: {
          color: '#333'
        },
        formatter: (params) => {
          const { time, hour, minute } = dataArray[params[0].dataIndex];
          const formattedTime = `${time} ${hour < 10 ? `0${hour}` : hour}:${minute === 0 ? '00' : minute}`;
          let content = `时间: ${formattedTime}<br/>`;
          params.forEach(item => {
            content += `${item.seriesName}: ${item.value} ${unit}<br/>`;
          });
          return content;
        }
      },
      legend: {
        data: [`输入流量 (${unit})`, `输出流量 (${unit})`, `输入拦截流量 (${unit})`],
        bottom: '5%',
        textStyle: {
          fontSize: 12,
          color: '#666'
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        },
        axisLabel: {
          color: '#666'
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        },
        axisLabel: {
          formatter: `{value} ${unit}`,
          color: '#666'
        }
      },
      series: [
        {
          name: `输入流量 (${unit})`,
          type: 'line',
          data: inSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#1890ff'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(24, 144, 255, 0.3)'
              }, {
                offset: 1, color: 'rgba(24, 144, 255, 0.1)'
              }],
            },
          },
        },
        {
          name: `输出流量 (${unit})`,
          type: 'line',
          data: outSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#52c41a'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(82, 196, 26, 0.3)'
              }, {
                offset: 1, color: 'rgba(82, 196, 26, 0.1)'
              }],
            },
          },
        },
        {
          name: `输入拦截流量 (${unit})`,
          type: 'line',
          data: inDropSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#ff4d4f'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(255, 77, 79, 0.3)'
              }, {
                offset: 1, color: 'rgba(255, 77, 79, 0.1)'
              }],
            },
          },
        }
      ]
    };
    chart.setOption(trafficOption);
  }, []);

  const drawConnectionChart = (chart, dataArray) => {
    const labels = dataArray.map(data => {
      const formattedHour = data.hour < 10 ? `0${data.hour}` : data.hour;
      const formattedMinute = data.minute === 0 ? '00' : data.minute;
      return `${formattedHour}:${formattedMinute}`; // 显示完整的时间
    });

    const maxInCount = dataArray.map(data => data.average_connect_in_count); // 最大输入新建连接
    const maxOutCount = dataArray.map(data => data.average_connect_out_count); // 最大输出新建连接
  
      const connectionOption = {
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '5%',
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderColor: '#d9d9d9',
      textStyle: {
        color: '#333'
      },
      formatter: (params) => {
        const { time, hour, minute } = dataArray[params[0].dataIndex];
        const formattedTime = `${time} ${hour < 10 ? `0${hour}` : hour}:${minute === 0 ? '00' : minute}`;
        let content = `时间: ${formattedTime}<br/>`;
        params.forEach(item => {
          content += `${item.seriesName}: ${item.value} 个<br/>`;
        });
        return content;
      }
    },
    legend: {
      data: ['最大输入新建连接', '最大输出新建连接'],
      bottom: '5%',
      textStyle: {
        fontSize: 12,
        color: '#666'
      }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: labels,
      axisLine: {
        lineStyle: {
          color: '#d9d9d9'
        }
      },
      axisLabel: {
        color: '#666'
      }
    },
    yAxis: {
      type: 'value',
      axisLine: {
        lineStyle: {
          color: '#d9d9d9'
        }
      },
      axisLabel: {
        color: '#666'
      }
    },
    series: [
      {
        name: '最大输入新建连接',
        type: 'line',
        data: maxInCount,
        smooth: true,
        lineStyle: {
          width: 2,
          color: '#722ed1'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(114, 46, 209, 0.3)'
            }, {
              offset: 1, color: 'rgba(114, 46, 209, 0.1)'
            }],
          },
        },
      },
      {
        name: '最大输出新建连接',
        type: 'line',
        data: maxOutCount,
        smooth: true,
        lineStyle: {
          width: 2,
          color: '#fa8c16'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(250, 140, 22, 0.3)'
            }, {
              offset: 1, color: 'rgba(250, 140, 22, 0.1)'
            }],
          },
        },
      }
    ]
  };
    chart.setOption(connectionOption);
  };

  useEffect(() => {
    if (trafficData.length > 0) {
      const trafficChart = echarts.init(document.getElementById('trafficChart'));
      const connectionChart = echarts.init(document.getElementById('connectionChart'));

      drawTrafficChart(trafficChart, trafficData);
      drawConnectionChart(connectionChart, trafficData);

      // 添加resize监听，确保图表大小自适应容器
      window.addEventListener('resize', () => {
        trafficChart.resize();
        connectionChart.resize();
      });

      // 销毁图表实例以防止内存泄漏
      return () => {
        trafficChart.dispose();
        connectionChart.dispose();
        window.removeEventListener('resize', () => {
          trafficChart.resize();
          connectionChart.resize();
        });
      };
    }
  }, [trafficData, drawTrafficChart]);

  return (
    <div style={{
      maxWidth: '1200px',
      padding: '30px 24px',
      margin: '0 auto'
    }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 标题和说明 */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 20, color: '#1890ff' }}>
            管理员流量查询(小墙)
          </Title>
          
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Alert
              message="支持自定义日期查询，时间跨度最多3天"
              type="info"
              showIcon
              style={{ 
                maxWidth: 400, 
                textAlign: 'center',
                border: '1px solid #91d5ff',
                borderRadius: 6
              }}
            />
          </div>
        </div>

        {/* 查询表单 */}
        <Row justify="center">
          <Col xs={24} md={20} lg={18}>
            <Card>
              <Form layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="开始时间">
                      <DatePicker
                        value={startTime}
                        onChange={setStartTime}
                        style={{ width: '100%' }}
                        placeholder="选择开始时间"
                        disabled={loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="结束时间">
                      <DatePicker
                        value={endTime}
                        onChange={setEndTime}
                        style={{ width: '100%' }}
                        placeholder="选择结束时间"
                        disabled={loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="IP地址">
                      <Input
                        placeholder="请输入查询IP地址"
                        value={ip}
                        onChange={(e) => setIp(e.target.value)}
                        onPressEnter={handleIpTrafficQuery}
                        disabled={loading}
                        addonAfter={
                          <Button 
                            type="primary"
                            onClick={handleIpTrafficQuery}
                            icon={<SearchOutlined />}
                            loading={loading}
                            disabled={loading || !ip.trim()}
                            size="small"
                            style={{ border: 'none' }}
                          >
                            查询
                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
        </Row>

        {/* 图表区域 */}
        {trafficData.length > 0 && (
          <Row justify="center">
            <Col xs={24} md={24} lg={24}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* 流量统计图表 */}
                <Card
                  title={
                    <span>
                      <LineChartOutlined style={{ marginRight: 8 }} />
                      流量统计图表
                    </span>
                  }
                  id="trafficCard"
                >
                  <div id="trafficChart" style={{ height: '400px', width: '100%' }}></div>
                </Card>
                
                {/* 连接统计图表 */}
                <Card
                  title={
                    <span>
                      <ApartmentOutlined style={{ marginRight: 8 }} />
                      连接统计图表
                    </span>
                  }
                  id="connectionCard"
                >
                  <div id="connectionChart" style={{ height: '400px', width: '100%' }}></div>
                </Card>
              </Space>
            </Col>
          </Row>
        )}
      </Space>
    </div>
  );
};

export default HuBeiXiaoQiangTrafficQuery;
